<template>
    <b-modal
            ref="modal"
            size="lg"
            :title="title"
            :visible="true"
            @hidden="$emit('hidden')"
            hide-footer
            no-close-on-esc
            no-close-on-backdrop
    >
        <ValidationObserver ref="observer" tag="form" @submit.prevent="handleSubmit()" v-slot="{ invalid }">
            <b-form-row v-if="!emailUuid && hasMailboxOAuthAccess">
                <b-col sm="3" lg="2">
                    <label class="required">{{$t('email.connection_type')}}</label>
                </b-col>

                <b-col sm="7" lg="8">
                    <b-form-group>
                        <b-form-radio-group
                                id="radio-auth-type"
                                v-model="form.config.authType"
                                :options="authTypeOptions"
                                stacked
                                plain
                        />
                    </b-form-group>
                </b-col>
            </b-form-row>

            <template v-if="isOAuthMode">
                <p v-if="isExistingOAuth" class="text-muted">{{$t('email.oauth_connected_info')}}</p>
                <p v-else class="text-muted">{{$t('email.oauth_connect_info')}}</p>

                <template v-if="requiresApplicationSelection">
                    <b-form-group :label="$t('email.select_application')">
                        <b-form-select v-model="selectedApplicationUuid" :options="applicationOptions" />
                    </b-form-group>
                    <b-alert v-if="selectedApplication && !selectedApplication.googleKeysConfigured" show variant="warning">
                        <p class="mb-2"><strong>{{ $t('email.no_google_keys_title') }}</strong></p>
                        <p class="mb-2">{{ $t('email.no_google_keys_instruction_admin') }}</p>
                        <p class="mb-1">{{ $t('email.no_google_keys_redirect_uri_label') }}</p>
                        <p class="mb-2"><code>{{ googleCallbackUrl }}</code></p>
                        <p class="mb-0">{{ $t('email.no_google_keys_instruction_bok') }}</p>
                    </b-alert>
                </template>

                <div class="text-center my-4">
                    <b-button variant="primary" @click.prevent="connectOAuth" :disabled="busy || connectDisabled">
                        <fa-icon v-if="busy" icon="spinner" spin />
                        <span v-else>{{ oauthConnectLabel }}</span>
                    </b-button>
                </div>
                <div class="text-right">
                    <button class="btn btn-link" @click.prevent="closeModal()" :disabled="busy">{{$t('_cancel')}}</button>
                </div>
            </template>

            <template v-else>
            <b-form-row>
                <b-col sm="3" lg="2">
                    <label class="required">{{$t('email.email_address')}}</label>
                </b-col>

                <b-col sm="7" lg="8">
                    <b-form-group>
                        <ValidationProvider rules="email|required" v-slot="{ classes, errors }">
                            <input v-model="form.email" type="text" class="form-control" :class="classes">
                            <span class="invalid-feedback">{{ errors[0] }}</span>
                        </ValidationProvider>
                    </b-form-group>
                </b-col>
            </b-form-row>

            <b-form-row>
                <b-col sm="3" lg="2">
                    <label class="required">{{$t('email.username')}}</label>
                </b-col>

                <b-col sm="7" lg="8">
                    <b-form-group>
                        <ValidationProvider rules="required" v-slot="{ classes, errors }">
                            <input v-model="form.config.username" class="form-control" :class="classes">
                            <span class="invalid-feedback">{{ errors[0] }}</span>
                        </ValidationProvider>
                    </b-form-group>
                </b-col>
            </b-form-row>

            <legend class="font-size-sm font-weight-bold">{{$t('email.inbox_settings_legend')}}</legend>

            <b-form-row>
                <b-col sm="3" lg="2">
                    <label>{{$t('email.protocol')}}</label>
                </b-col>

                <b-col sm="7" lg="8">
                    <b-form-group>
                        <b-form-radio-group
                                id="radio-inbox-protocol"
                                v-model="form.config.inbox.protocol"
                                :options="inboxProtocols"
                                plain
                        />
                    </b-form-group>
                </b-col>
            </b-form-row>

            <b-form-row>
                <b-col sm="3" lg="2">
                    <label class="required">{{$t('email.server_address')}}</label>
                </b-col>

                <b-col sm="7" lg="8">
                    <b-form-group>
                        <ValidationProvider rules="required" v-slot="{ classes, errors }">
                            <input v-model="form.config.inbox.host" class="form-control" :class="classes">
                            <span class="invalid-feedback">{{ errors[0] }}</span>
                        </ValidationProvider>
                    </b-form-group>
                </b-col>
            </b-form-row>

            <b-form-row>
                <b-col sm="3" lg="2">
                    <label :class="emailUuid ? '' : 'required'">{{$t('email.password')}}</label>
                </b-col>

                <b-col sm="7" lg="8">
                    <b-form-group>
                        <ValidationProvider :rules="emailUuid ? '' : 'required'" v-slot="{ classes, errors }">
                            <input type="password" v-model="form.config.password" class="form-control" :class="classes">
                            <span class="invalid-feedback">{{ errors[0] }}</span>
                        </ValidationProvider>
                    </b-form-group>
                </b-col>
            </b-form-row>

            <b-form-row>
                <b-col sm="3" lg="2">
                    <label class="required">{{$t('email.port')}}</label>
                </b-col>

                <b-col sm="3" lg="4">
                    <b-form-group>
                        <ValidationProvider rules="required" v-slot="{ classes, errors }">
                            <input type="number" v-model="form.config.inbox.port" class="form-control" :class="classes">
                            <span class="invalid-feedback">{{ errors[0] }}</span>
                        </ValidationProvider>
                    </b-form-group>
                </b-col>
            </b-form-row>

            <b-form-row>
                <b-col sm="3" lg="2">
                    <label>{{$t('email.encryption')}}</label>
                </b-col>

                <b-col sm="7" lg="8">
                    <b-form-group>
                            <b-form-radio-group
                                    id="radio-inbox-ssl"
                                    v-model="form.config.inbox.ssl"
                                    :options="sslOptions"
                                    plain
                            />
                    </b-form-group>
                </b-col>
            </b-form-row>

            <legend class="font-size-sm font-weight-bold">{{$t('email.outbox_settings_legend')}}</legend>

            <b-form-row>
                <b-col sm="3" lg="2">
                    <label class="required">{{$t('email.server_address')}}</label>
                </b-col>

                <b-col sm="7" lg="8">
                    <b-form-group>
                        <ValidationProvider rules="required" v-slot="{ classes, errors }">
                            <input v-model="form.config.outbox.host" class="form-control" :class="classes">
                            <span class="invalid-feedback">{{ errors[0] }}</span>
                        </ValidationProvider>
                    </b-form-group>
                </b-col>
            </b-form-row>

            <b-form-row>
                <b-col sm="3" lg="2">
                    <label class="required">{{$t('email.port')}}</label>
                </b-col>

                <b-col sm="4" lg="3">
                    <b-form-group>
                        <ValidationProvider rules="required" v-slot="{ classes, errors }">
                            <input type="number" v-model="form.config.outbox.port" class="form-control"
                                   :class="classes">
                            <span class="invalid-feedback">{{ errors[0] }}</span>
                        </ValidationProvider>
                    </b-form-group>
                </b-col>
            </b-form-row>

            <b-form-row>
                <b-col sm="3" lg="2">
                    <label>{{$t('email.encryption')}}</label>
                </b-col>

                <b-col sm="7" lg="8">
                    <b-form-group>
                        <b-form-radio-group
                                id="radio-outbox-ssl"
                                v-model="form.config.outbox.ssl"
                                :options="sslOptions"
                                plain
                        />
                    </b-form-group>
                </b-col>
            </b-form-row>

            <div class="text-right">
                <button class="btn btn-link mr-2" @click.prevent="closeModal()" :disabled="busy">{{$t('_cancel')}}</button>
                <b-button variant="primary" size="sm" @click.prevent="handleSubmit" :disabled="busy">
                    <fa-icon v-if="busy" icon="spinner" spin />
                    <span v-else>{{ emailUuid ? $t('_save') : $t('email.add_email_account_button') }}</span>
                </b-button>
            </div>
            </template>

        </ValidationObserver>

    </b-modal>
</template>

<script>
    import {mapGetters} from "vuex";
    import {ValidationObserver, ValidationProvider, configure, extend} from 'vee-validate';
    import {required, email} from 'vee-validate/dist/rules';
    import i18n from "@/i18n";
    extend('email', {
        ...email,
        message: i18n.t('email.provide_correct_email_validation')
    });

    extend('required', {
        ...required,
        message: i18n.t('email.this_field_is_required')
    });

    configure({
        classes: {
            valid: 'is-valid',
            invalid: 'is-invalid'
        }
    });

    export default {
        name: "EmailsModal",
        components: {ValidationProvider, ValidationObserver},
        props: {
            emailUuid: {
                type: String,
                required: false,
            }
        },

        data() {
            return {
                formState: null,
                busy: false,

                oauthApplications: [],
                selectedApplicationUuid: null,
                applicationsLoaded: false,

                form: {
                    email: '',
                    config: {
                        authType: 'PASSWORD',
                        username: '',
                        password: '',
                        inbox: {
                            host: '',
                            port: 143,
                            protocol: 'IMAP',
                            ssl: 'NONE'
                        },
                        outbox: {
                            host: '',
                            port: 25,
                            protocol: 'SMTP',
                            ssl: 'NONE'
                        }
                    },
                },

                inboxProtocols: ['IMAP'],
                sslOptions: [
                    { text: 'Brak', value: 'NONE' },
                    { text: 'STARTTLS', value: 'STARTTLS' },
                    { text: 'SSL/TLS', value: 'SSL/TLS' },
                ],
            }
        },

        methods: {
            closeModal() {
                this.$refs['modal'].hide()
            },
            async handleSubmit() {
                const valid = await this.$refs.observer.validate();
                if (!valid || this.busy) {
                    return
                }
                this.busy = true;
                // authType is a UI-only switch; the password create/update API
                // (MailboxConfigType) rejects unknown fields, so strip it out.
                const config = {...this.form.config};
                delete config.authType;
                const account = {...this.form, config};
                this.$store
                    .dispatch('Email/updateAccount', {account, uuid: this.emailUuid})
                    .then(() => this.closeModal())
                    .finally(() => this.busy = false);
            },
            connectOAuth() {
                this.busy = true;
                const payload = {provider: this.oauthProvider};
                // Only Gmail uses per-application (BYO) keys; MS365 stays on the static app.
                if (this.oauthProvider === 'google' && this.selectedApplicationUuid) {
                    payload.applicationUuid = this.selectedApplicationUuid;
                }
                this.$store.dispatch('Email/connectOAuth', payload)
                    .then((url) => { if (url) { window.location.href = url; } })
                    .finally(() => this.busy = false);
            },
            loadOAuthApplications() {
                if (this.applicationsLoaded) {
                    return;
                }
                this.$store.dispatch('Email/fetchOAuthApplications')
                    .then((applications) => {
                        this.oauthApplications = applications || [];
                        this.applicationsLoaded = true;
                        // Auto-select when the user belongs to exactly one application.
                        if (this.oauthApplications.length === 1) {
                            this.selectedApplicationUuid = this.oauthApplications[0].uuid;
                        }
                    })
                    .catch(() => {});
            },
            getConfiguration(emailUuid) {
                this.busy = true;
                return this.$store.dispatch('Email/fetchConfig', emailUuid)
                    .then(({data}) => {
                        this.form = {
                            email: data.data.email,
                            footer: data.data.footer,
                            config: data.data.config,
                        }
                    })
                    .finally(() => this.busy = false)
            }
        },

        created() {
            // The mailbox OAuth gate reads the user's applications from the Auth store. On a
            // direct visit to /profile/email-boxes that store may not be populated yet, so make
            // sure it is — until it loads hasMailboxOAuthAccess stays false (fail closed).
            if (this.$store.state.Auth.applications.length === 0) {
                this.$store.dispatch('Auth/fetchApplications').catch(() => {});
            }

            if (this.emailUuid) {
                this.getConfiguration(this.emailUuid);
            }
        },

        computed: {
            title() {
                return this.emailUuid ? this.$t('email.email_edit_title') : this.$t('email.email_add_title');
            },

            authTypeOptions() {
                const options = [
                    { text: this.$t('email.connection_type_password'), value: 'PASSWORD' },
                ];

                // OAuth (Gmail/MS365) is temporarily limited to an allow-list of application
                // types (see MAILBOX_OAUTH_APPLICATION_TYPES / CU-869e2vev8) — hide the options
                // for everyone else so they only see the classic password connection.
                if (this.hasMailboxOAuthAccess) {
                    options.push(
                        { text: this.$t('email.connection_type_gmail'), value: 'OAUTH_GMAIL' },
                        { text: this.$t('email.connection_type_ms365'), value: 'OAUTH_MS365' },
                    );
                }

                return options;
            },

            isOAuthMode() {
                return ['OAUTH', 'OAUTH_GMAIL', 'OAUTH_MS365'].includes(this.form.config.authType);
            },

            isExistingOAuth() {
                return this.form.config.authType === 'OAUTH';
            },

            oauthProvider() {
                if (this.form.config.authType === 'OAUTH_GMAIL') return 'google';
                if (this.form.config.authType === 'OAUTH_MS365') return 'microsoft';
                // existing OAuth account (authType === 'OAUTH'): derive from stored provider
                return this.form.config.provider === 'GMAIL' ? 'google' : 'microsoft';
            },

            oauthConnectLabel() {
                return this.oauthProvider === 'google'
                    ? this.$t('email.connect_with_google')
                    : this.$t('email.connect_with_microsoft');
            },

            // BYO Gmail (new connect) requires picking the application whose Google keys to use.
            requiresApplicationSelection() {
                return this.isOAuthMode && this.oauthProvider === 'google' && !this.isExistingOAuth;
            },

            applicationOptions() {
                return this.oauthApplications.map(application => ({
                    value: application.uuid,
                    text: application.name,
                }));
            },

            selectedApplication() {
                return this.oauthApplications.find(application => application.uuid === this.selectedApplicationUuid) || null;
            },

            // Exact redirect URI the dealer must register in their own Google Cloud OAuth project.
            // Built from the same API host the backend serves the callback on (VUE_APP_DX_URL).
            googleCallbackUrl() {
                return `${process.env.VUE_APP_DX_URL}/mailbox/oauth/callback/google`;
            },

            connectDisabled() {
                if (!this.requiresApplicationSelection) {
                    return false;
                }
                // Block until an application with configured Google keys is selected.
                return !this.selectedApplication || !this.selectedApplication.googleKeysConfigured;
            },

            ...mapGetters('Email', ['config']),
            ...mapGetters('Auth', ['hasMailboxOAuthAccess'])
        },

        watch: {
            'form.config.authType'(authType) {
                if (authType === 'OAUTH_GMAIL') {
                    this.loadOAuthApplications();
                }
            }
        },
    }
</script>

<style scoped lang="scss">
    label.required::after {
        content: '*';
        color: red;
        padding-left: 5px;
    }
</style>